import { UserRole } from "@joonapp/web-shared"
import { useQuery } from "@tanstack/react-query"

import {
  getCoachDirectReports,
  getCoachingGroups,
  getCoachingSubscription,
  getDeactivatedCoachingGroups,
} from "./api"
import useAdmin from "../../pages/admin/useAdmin"
import useCurrentClient from "../../pages/clients/hooks/useCurrentClient"
import { QueryKey } from "../../types/queries"
import { isJoonCoachingGroup } from "../../util/misc"

export const useCoachingGroupsQuery = () => {
  const hideJoonCoachingGroups = useAdmin()

  return useQuery({
    queryKey: [QueryKey.COACHING_GROUPS],
    queryFn: getCoachingGroups,
    select: (data) =>
      hideJoonCoachingGroups
        ? data.filter((group) => !isJoonCoachingGroup(group))
        : data,
  })
}

export const useCoachingSubscriptionQuery = (clientUserId?: number) => {
  const { currentClientProfile } = useCurrentClient()
  const clientId = clientUserId || currentClientProfile?.user.id

  return useQuery({
    queryKey: [QueryKey.COACHING_SUBSCRIPTION, clientId],
    queryFn: () => getCoachingSubscription(clientId!),
    enabled: !!clientId,
  })
}

// get all coach subscriptions for client ids, and mark with client id and coach id
export const useCoachSubscriptions = (coachUserId?: number) => {
  const { data: coachingGroups } = useCoachingGroupsQuery()
  const clientCoachUserIds = coachingGroups?.map((group) => {
    const profiles = group.profiles
    const client = profiles.find((profile) => profile.role === UserRole.CLIENT)
    const coach = profiles.find(
      (profile) =>
        profile.role === UserRole.COACH && profile.user.id !== 1458884
    )
    return {
      clientId: client?.user.id,
      coachId: coach?.user.id,
    }
  })

  const getAllSubscriptions = async () => {
    if (!clientCoachUserIds || clientCoachUserIds.length === 0) return []

    const results = await Promise.all(
      clientCoachUserIds.map(({ clientId }) =>
        getCoachingSubscription(clientId!)
      )
    )

    return results.map((result, index) => ({
      result,
      clientId: clientCoachUserIds[index].clientId,
      coachId: clientCoachUserIds[index].coachId,
    }))
  }

  return useQuery({
    queryKey: [QueryKey.COACHING_SUBSCRIPTIONS, clientCoachUserIds],
    queryFn: getAllSubscriptions,
    enabled: !!clientCoachUserIds,
    select: (data) =>
      data.filter((item) => !coachUserId || item.coachId === coachUserId),
  })
}

export const useDeactivatedCoachingGroupsQuery = () => {
  return useQuery({
    queryKey: [QueryKey.DEACTIVATED_COACHING_GROUPS],
    queryFn: getDeactivatedCoachingGroups,
  })
}

export const useCoachDirectReportsQuery = () => {
  return useQuery({
    queryKey: [QueryKey.COACH_DIRECT_REPORTS],
    queryFn: () => getCoachDirectReports(),
  })
}
